<template>
  <div>
    <el-form size="mini" ref="form" label-position="left" label-width="170px" v-loading="loading" element-loading-spinner="atom-audit-loader">
      <el-row :gutter="30">
        <el-col :span="12">
          <h4 class="headerWithBorder">Chart Demographic</h4>

          <el-form-item size="mini" label="Patient Number">
            <el-input disabled size="mini" :maxlength="15" type="text" id="patientNumber" v-model="worksheet.patientNumber"> </el-input>
          </el-form-item>

          <el-form-item size="mini" label="MR Number">
            <el-input :disabled="disabled" size="mini" :maxlength="15" type="text" id="mrNumber" v-model="worksheet.mrnumber"> </el-input>
          </el-form-item>

          <el-form-item size="mini" label="Payor">
            <el-input :disabled="disabled" v-model="worksheet.payorName" placeholder="Payor" itemkey="key" label="value"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Payor Detail">
            <el-input :disabled="disabled" v-model="worksheet.payorDetail" placeholder="Payor Detail" itemkey="key" label="value"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Date of Service">
            <el-date-picker :disabled="disabled" size="mini" type="date" id="dischargeDate" v-model="worksheet.dateOfService" format="MM/dd/yyyy"> </el-date-picker>
          </el-form-item>

          <el-form-item size="mini" label="Age">
            <el-input disabled v-model="worksheet.patientAge"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Patient Sex">
            <el-input disabled v-model="worksheet.sex"></el-input>
          </el-form-item>
        </el-col>

        <el-col :span="12">
          <h4 class="headerWithBorder">Review Information</h4>

          <el-form-item size="mini" label="Facility">
            <el-input disabled v-model="worksheet.clientName"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Auditor">
            <el-input disabled v-model="worksheet.reviewerName"></el-input>
          </el-form-item>

          <el-form-item v-if="worksheet.projectReviewTypeId === 2" size="mini" label="Original Auditor">
            <el-input disabled v-model="worksheet.originalAuditor"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Review Date">
            <el-date-picker disabled size="mini" type="date" id="revDate" v-model="worksheet.reviewDate" format="MM/dd/yyyy"> </el-date-picker>
          </el-form-item>

          <el-form-item size="mini" label="Coder">
            <el-input disabled v-model="worksheet.coder"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Provider">
            <el-input disabled v-model="worksheet.physicianName"></el-input>
          </el-form-item>
          <el-form-item size="mini" label="Audit Result" v-if="worksheet.auditResult">
            <div class="auditResultRow">
              <el-tag :type="auditResultTagType" size="mini" effect="light" :title="auditResultTitle">
                {{ worksheet.auditResult }}
              </el-tag>
              <el-button class="auditResultAction" type="text" size="mini" icon="mdi mdi-table-eye" @click="showSummary" :title="auditResultTitle">
                View stats
              </el-button>
            </div>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12" class="top-margin">
          <h4 class="headerWithBorder">Original Coding Details</h4>

          <el-form-item size="mini" label="Place of Service">
            <el-input disabled v-model="worksheet.placeOfService"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Patient Status">
            <el-input disabled size="mini" id="originalDrg" v-model="worksheet.patientStatusCalc"> </el-input>
          </el-form-item>

          <el-form-item size="mini" label="EM Guidelines">
            <el-input :disabled="disabled" size="mini" id="originalWeight" v-model="worksheet.emGuidelines"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <el-dialog title="Record Summary" :visible.sync="summaryDialogVisible" destroy-on-close width="75%">
      <div style="min-height: 475px;" v-loading="summaryLoading" element-loading-spinner="atom-audit-loader">
        <template v-if="!summaryLoading">
          <div v-for="row in summaryData" :key="row.patientType" style="margin-bottom: 50px;">
            <PROSummary :ref="`proSummary_${row.codeDesc}`" :item="row" :title="`${row.codeDesc}`"></PROSummary>
          </div>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<script>
  import dateHelper from '@/mixins/date-helper'
  import { format, parseISO } from 'date-fns'
  import { mapMutations, mapState } from 'vuex'
  import PROSummary from '@/views/reports/dashboards/PRO_Summary'
  import axios from '@/utilities/axios-global'

  export default {
    name: 'coderInpatientDemographics',
    mixins: [dateHelper],
    props: ['worksheet'],
    data() {
      return {
        format: format,
        parseISO: parseISO,
        disabled: true,
        loading: false,
        summaryLoading: true,
        summaryDialogVisible: false,
        summaryData: {}
      }
    },
    components: {
      PROSummary
    },
    methods: {
      ...mapMutations('coder/', ['SET_RESPONSE_PATSTAT', 'SET_RESPONSE_DRG', 'SET_RESPONSE_POO']),
      async showSummary() {
        if (!this.worksheet?.proRecordId) return

        this.summaryDialogVisible = true
        const payload = {
          proRecordIds: this.worksheet.proRecordId,
          includeAccts: false,
          statType: 'test'
        }

        try {
          this.summaryLoading = true
          const response = await axios.post(this.baseApiUrl + '/reports/dashboard/proBasicStat', payload)
          this.summaryData = response.data
        } catch (err) {
          this.summaryData = []
        } finally {
          this.summaryLoading = false
        }
      }
    },
    computed: {
      ...mapState('user/', ['baseApiUrl']),
      auditResultTagType() {
        switch (this.worksheet?.auditResult) {
          case 'No Change':
            return 'success'
          case 'FYI':
            return 'warning'
          case 'RVU Change':
            return 'danger'
          default:
            return 'info'
        }
      },
      auditResultTitle() {
        switch (this.worksheet?.auditResult) {
          case 'No Change':
            return 'There are no recommended changes on the record'
          case 'FYI':
            return 'The record contains recommendations that do not affect the grouping'
          case 'RVU Change':
            return 'The record contains recommendations that affect the RVU'
          default:
            return ''
        }
      }
    }
  }
</script>

<style scoped>
  .auditResultRow {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 22px;
  }

  .auditResultAction {
    padding: 0;
    line-height: 1;
  }
</style>
